/**
 * Header Dropdown Navigation
 *
 * Handles desktop flyout dropdowns, carousel scrolling (via Swiper),
 * 3rd-level drilldown, "alle Kategorien" panel, search, and mobile full-screen menu.
 */

import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import { createSearchResultCard as createSectionSearchCard } from './search-result-card';

// Must stay in sync with $desktop-breakpoint in header_dropdown_navigation.scss.
const DESKTOP_BREAKPOINT = 1440;
const CLOSE_DELAY_MS = 200;
const SLIDES_PER_VIEW = 5;
const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_MIN_CHARS = 3;
// When the flyout's data-search-mode is one of these, the header search hits
// the section_form endpoints (is_ankauf=1 / is_repair=1) and renders a flat
// list of card_product_search_result cards instead of the two-column flyout.
const SEARCH_MODE_VERKAUFEN = 'verkaufen';
const SEARCH_MODE_REPARATUR = 'reparatur';
const SECTION_SEARCH_FORM_TYPE = {
  [SEARCH_MODE_VERKAUFEN]: 'sell',
  [SEARCH_MODE_REPARATUR]: 'repair',
};
const SECTION_SEARCH_QUERY_PARAM = {
  [SEARCH_MODE_VERKAUFEN]: 'is_ankauf=1',
  [SEARCH_MODE_REPARATUR]: 'is_repair=1',
};
const SECTION_SEARCH_MAX_RESULTS = 5;
// Clear-then-announce delay so assistive tech reliably picks up the new
// textContent on a persistent aria-live region (some screen readers ignore
// updates when the region already matches the incoming text).
const SR_ANNOUNCE_DELAY_MS = 100;

// ----- State -----
let openFlyoutId = null;
let closeTimer = null;
let mobileFocusTrapHandler = null;
let mobileEscapeHandler = null;
// Set by initMobileSearch so any drilldown-close path can drop a pending search
// without reaching into that closure (see closeMobileDrilldown).
let cancelMobileSearchLoad = null;

// ----- Cached DOM references (populated in init) -----
let cachedNav = null;
let cachedFlyouts = null;
let cachedTriggers = null;
let cachedAllCategoriesBtn = null;
let cachedMobileMenu = null;
let cachedMobileCloseBtn = null;

// =========================================================================
// SVG Helpers
// =========================================================================

const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * Builds a Lucide-style SVG root with the standard stroke attributes.
 * Mirrors the output of the server-side lucideIcon() Twig function.
 */
function createLucideSvgRoot(iconName, extraClass = '') {
  const svg = document.createElementNS(SVG_NS, 'svg');
  const classes = ['lucide', 'lucide-' + iconName];
  if (extraClass) {
    classes.push(extraClass);
  }
  svg.setAttribute('class', classes.join(' '));
  svg.setAttribute('width', '24');
  svg.setAttribute('height', '24');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('aria-hidden', 'true');
  return svg;
}

function appendSvgPath(svg, d) {
  const path = document.createElementNS(SVG_NS, 'path');
  path.setAttribute('d', d);
  svg.appendChild(path);
}

function createArrowUpRightIcon(extraClass = '') {
  const svg = createLucideSvgRoot('arrow-up-right', extraClass);
  appendSvgPath(svg, 'M7 7h10v10');
  appendSvgPath(svg, 'M7 17 17 7');
  return svg;
}

/**
 * Injects up to `max` server-rendered cards (item.card_html from /findTypes.php)
 * into the container.
 */
function renderCardHtmlInto(container, results, max) {
  if (!container) {
    return;
  }
  container.innerHTML = '';
  let shown = 0;
  results.forEach(function (item) {
    if (shown >= max || !item.card_html) {
      return;
    }
    container.insertAdjacentHTML('beforeend', item.card_html);
    shown++;
  });
}

// =========================================================================
// Accessibility Utilities
// =========================================================================

function announceToScreenReader(message) {
  let announcer = document.getElementById('header-sr-announcer');
  if (!announcer) {
    announcer = document.createElement('div');
    announcer.id = 'header-sr-announcer';
    announcer.setAttribute('aria-live', 'polite');
    announcer.setAttribute('aria-atomic', 'true');
    announcer.className = 'sr_only';
    document.body.appendChild(announcer);
  }
  announcer.textContent = '';
  setTimeout(function () {
    announcer.textContent = message;
  }, SR_ANNOUNCE_DELAY_MS);
}

function bindMobileFocusTrap() {
  if (mobileFocusTrapHandler) {
    return;
  }
  mobileFocusTrapHandler = function (e) {
    if (isDesktop() || e.key !== 'Tab' || !cachedMobileMenu) {
      return;
    }
    if (cachedMobileMenu.getAttribute('aria-hidden') !== 'false') {
      return;
    }

    const visiblePanel = cachedMobileMenu.querySelector('.header_dropdown_nav__mobile_drilldown.is_open');
    const scope = visiblePanel || cachedMobileMenu;
    const focusables = Array.from(
      scope.querySelectorAll('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'),
    ).filter(function (el) {
      return !el.hasAttribute('disabled') && el.offsetParent !== null;
    });

    if (focusables.length === 0) {
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    } else if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    }
  };
  document.addEventListener('keydown', mobileFocusTrapHandler);
}

function unbindMobileFocusTrap() {
  if (mobileFocusTrapHandler) {
    document.removeEventListener('keydown', mobileFocusTrapHandler);
    mobileFocusTrapHandler = null;
  }
}

function bindMobileEscape() {
  if (mobileEscapeHandler) {
    return;
  }
  mobileEscapeHandler = function (e) {
    if (e.key !== 'Escape' || !cachedMobileMenu) {
      return;
    }
    if (cachedMobileMenu.getAttribute('aria-hidden') !== 'false') {
      return;
    }
    // Close drilldown panels first, then the menu itself.
    const openDrilldown = cachedMobileMenu.querySelector('.header_dropdown_nav__mobile_drilldown.is_open');
    if (openDrilldown) {
      closeMobileDrilldown(openDrilldown);
    } else {
      closeMobileMenu();
    }
  };
  document.addEventListener('keydown', mobileEscapeHandler);
}

function unbindMobileEscape() {
  if (mobileEscapeHandler) {
    document.removeEventListener('keydown', mobileEscapeHandler);
    mobileEscapeHandler = null;
  }
}

// =========================================================================
// Desktop Flyout
// =========================================================================

function isDesktop() {
  return window.innerWidth >= DESKTOP_BREAKPOINT;
}

function openFlyout(id) {
  clearTimeout(closeTimer);
  if (openFlyoutId === id) {
    return;
  }

  closeAllFlyouts();
  const flyout = document.getElementById(id);
  if (!flyout) {
    return;
  }

  flyout.removeAttribute('hidden');
  void flyout.offsetHeight;
  flyout.classList.add('is_open');
  openFlyoutId = id;

  const trigger = document.querySelector(`[data-flyout="${id}"]`);
  if (trigger) {
    trigger.setAttribute('aria-expanded', 'true');
  }

  const label = flyout.getAttribute('aria-label') || '';
  announceToScreenReader(label + ' geöffnet');

  const level3 = flyout.querySelector('.js_dropdown_level3');
  const level2 = flyout.querySelector(
    '.header_dropdown_nav__flyout_content:not(.header_dropdown_nav__flyout_content--level3)',
  );
  if (level3 && level2) {
    level3.hidden = true;
    level2.hidden = false;
  }

  initSwipers(flyout);
}

function closeFlyout(id) {
  const flyout = document.getElementById(id);
  if (!flyout) {
    return;
  }

  const label = flyout.getAttribute('aria-label') || '';

  flyout.classList.remove('is_open');
  flyout.setAttribute('hidden', '');
  openFlyoutId = null;

  const trigger = document.querySelector(`[data-flyout="${id}"]`);
  if (trigger) {
    trigger.setAttribute('aria-expanded', 'false');
  }

  if (label) {
    announceToScreenReader(label + ' geschlossen');
  }
}

function closeAllFlyouts() {
  cachedFlyouts.forEach(function (flyout) {
    flyout.classList.remove('is_open');
    flyout.setAttribute('hidden', '');
  });
  cachedTriggers.forEach(function (trigger) {
    trigger.setAttribute('aria-expanded', 'false');
  });
  if (cachedAllCategoriesBtn) {
    cachedAllCategoriesBtn.setAttribute('aria-expanded', 'false');
  }
  openFlyoutId = null;
}

function scheduleClose() {
  closeTimer = setTimeout(function () {
    closeAllFlyouts();
  }, CLOSE_DELAY_MS);
}

function cancelClose() {
  clearTimeout(closeTimer);
}

function initDesktopListeners() {
  cachedTriggers.forEach(function (trigger) {
    const flyoutId = trigger.dataset.flyout;

    // Triggers without a flyout still close any open flyout so the previous submenu doesn't linger.
    trigger.addEventListener('mouseenter', function () {
      if (!isDesktop()) {
        return;
      }
      if (flyoutId) {
        openFlyout(flyoutId);
      } else {
        closeAllFlyouts();
      }
    });

    trigger.addEventListener('focus', function () {
      if (!isDesktop()) {
        return;
      }
      if (flyoutId) {
        openFlyout(flyoutId);
      } else {
        closeAllFlyouts();
      }
    });
  });

  if (cachedAllCategoriesBtn) {
    cachedAllCategoriesBtn.addEventListener('mouseenter', function () {
      if (!isDesktop()) {
        return;
      }
      openFlyout('flyout-all-categories');
    });
    cachedAllCategoriesBtn.addEventListener('focus', function () {
      if (!isDesktop()) {
        return;
      }
      openFlyout('flyout-all-categories');
    });
    cachedAllCategoriesBtn.addEventListener('click', function (e) {
      e.preventDefault();
      if (!isDesktop()) {
        return;
      }
      const id = 'flyout-all-categories';
      if (openFlyoutId === id) {
        closeFlyout(id);
      } else {
        openFlyout(id);
      }
    });
  }

  if (cachedNav) {
    cachedNav.addEventListener('mouseleave', function () {
      if (!isDesktop()) {
        return;
      }
      scheduleClose();
    });
    cachedNav.addEventListener('mouseenter', function () {
      cancelClose();
    });
  }

  cachedFlyouts.forEach(function (flyout) {
    flyout.addEventListener('mouseenter', cancelClose);
    flyout.addEventListener('mouseleave', function () {
      if (!isDesktop()) {
        return;
      }
      scheduleClose();
    });

    // Keyboard navigation inside flyouts
    flyout.addEventListener('keydown', function (e) {
      if (!isDesktop()) {
        return;
      }
      const focusables = Array.from(flyout.querySelectorAll('a:not([hidden]), button:not([hidden])')).filter(
        function (el) {
          return el.offsetParent !== null;
        },
      );
      const idx = focusables.indexOf(document.activeElement);

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = idx < focusables.length - 1 ? focusables[idx + 1] : focusables[0];
        next.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = idx > 0 ? focusables[idx - 1] : focusables[focusables.length - 1];
        prev.focus();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        const triggerId = flyout.id;
        closeAllFlyouts();
        const trigger = document.querySelector('[data-flyout="' + triggerId + '"]');
        if (trigger) {
          trigger.focus();
        }
      }
    });
  });

  const triggers = [...cachedTriggers];
  triggers.forEach(function (trigger, index) {
    trigger.addEventListener('keydown', function (e) {
      if (!isDesktop()) {
        return;
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        const next = triggers[index + 1] || triggers[0];
        next.focus();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        const prev = triggers[index - 1] || triggers[triggers.length - 1];
        prev.focus();
      } else if (e.key === 'ArrowDown' || e.key === 'Enter') {
        const flyoutId = trigger.dataset.flyout;
        if (flyoutId) {
          e.preventDefault();
          openFlyout(flyoutId);
          const flyout = document.getElementById(flyoutId);
          const firstLink = flyout ? flyout.querySelector('a, button') : null;
          if (firstLink) {
            firstLink.focus();
          }
        }
      }
    });
  });

  // "alle Kategorien" button mirrors the trigger-level keyboard pattern:
  // ArrowDown / Enter opens the flyout and moves focus inside.
  if (cachedAllCategoriesBtn) {
    cachedAllCategoriesBtn.addEventListener('keydown', function (e) {
      if (!isDesktop()) {
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        const id = 'flyout-all-categories';
        openFlyout(id);
        const flyout = document.getElementById(id);
        const firstLink = flyout ? flyout.querySelector('a, button') : null;
        if (firstLink) {
          firstLink.focus();
        }
      }
    });
  }
}

// =========================================================================
// Carousel (Swiper)
// =========================================================================

function initSwiper(container) {
  if (container.swiper) {
    return;
  }

  try {
    new Swiper(container, {
      modules: [Navigation, Pagination],
      slidesPerView: 'auto',
      slidesPerGroup: SLIDES_PER_VIEW,
      spaceBetween: 24,
      observer: true,
      observeParents: true,
      navigation: {
        nextEl: container.querySelector('.js_carousel__arrow--next'),
        prevEl: container.querySelector('.js_carousel__arrow--prev'),
      },
      pagination: {
        el: container.querySelector('.js_carousel__pagination'),
        clickable: true,
      },
    });
  } catch (err) {
    console.error('Swiper init failed:', err);
  }
}

function initSwipers(flyoutEl) {
  flyoutEl.querySelectorAll('.js_dropdown_carousel').forEach(function (container) {
    initSwiper(container);
  });
}

// =========================================================================
// 3rd Level Drilldown
// =========================================================================

function initDrilldown() {
  document.querySelectorAll('.js_dropdown_card_drilldown').forEach(function (card) {
    card.addEventListener('click', function (e) {
      if (!isDesktop()) {
        return;
      }
      e.preventDefault();

      const children = JSON.parse(card.dataset.children || '[]');
      const parentName = card.dataset.parentName;
      const childName = card.dataset.childName;

      const flyout = card.closest('.js_dropdown_nav_flyout');
      if (!flyout) {
        return;
      }

      const level2 = flyout.querySelector(
        '.header_dropdown_nav__flyout_content:not(.header_dropdown_nav__flyout_content--level3)',
      );
      const level3 = flyout.querySelector('.js_dropdown_level3');
      if (!level2 || !level3) {
        return;
      }

      const backParent = level3.querySelector('.js_dropdown_back_parent_name');
      const breadcrumbChild = level3.querySelector('.js_dropdown_breadcrumb_child');
      const viewAll = level3.querySelector('.js_dropdown_level3_view_all');

      if (backParent) {
        backParent.textContent = parentName;
      }
      if (breadcrumbChild) {
        breadcrumbChild.textContent = childName;
      }
      if (viewAll) {
        viewAll.href = card.href;
        viewAll.setAttribute('aria-label', 'Alle ' + childName + '-Produkte ansehen');
      }

      const cardsContainer = level3.querySelector('.js_dropdown_level3_cards');
      if (cardsContainer) {
        cardsContainer.innerHTML = '';
        children.forEach(function (child) {
          const slide = document.createElement('div');
          slide.className = 'swiper-slide';

          const card = document.createElement('a');
          card.href = child.url;
          card.className = 'header_dropdown_nav__card';

          const imgDiv = document.createElement('div');
          imgDiv.className = 'header_dropdown_nav__card_image';
          if (child.imageUrl) {
            const img = document.createElement('img');
            img.src = child.imageUrl;
            img.alt = child.name;
            img.loading = 'lazy';
            imgDiv.appendChild(img);
          }

          const label = document.createElement('span');
          label.className = 'header_dropdown_nav__card_label';
          label.textContent = child.name;

          card.appendChild(imgDiv);
          card.appendChild(label);
          slide.appendChild(card);
          cardsContainer.appendChild(slide);
        });
      }

      level2.hidden = true;
      level3.hidden = false;

      // Init or update Swiper for the 3rd-level carousel
      const level3Carousel = level3.querySelector('.js_dropdown_level3_carousel');
      if (level3Carousel) {
        if (level3Carousel.swiper) {
          level3Carousel.swiper.destroy(true, true);
        }
        initSwiper(level3Carousel);
      }
    });
  });

  document.querySelectorAll('.js_dropdown_back_btn').forEach(function (btn) {
    btn.addEventListener('click', function () {
      const flyout = btn.closest('.js_dropdown_nav_flyout');
      if (!flyout) {
        return;
      }

      const level2 = flyout.querySelector(
        '.header_dropdown_nav__flyout_content:not(.header_dropdown_nav__flyout_content--level3)',
      );
      const level3 = flyout.querySelector('.js_dropdown_level3');

      if (level2) {
        level2.hidden = false;
      }
      if (level3) {
        level3.hidden = true;
      }
    });
  });
}

// =========================================================================
// Mobile Menu
// =========================================================================

function initMobileMenu() {
  const mobileOpenBtns = document.querySelectorAll('.js_h_mobile_menu');
  mobileOpenBtns.forEach(function (btn) {
    btn.addEventListener('click', function () {
      if (isDesktop()) {
        return;
      }
      openMobileMenu();
    });
  });

  if (cachedMobileCloseBtn) {
    cachedMobileCloseBtn.addEventListener('click', closeMobileMenu);
  }

  // Desktop flyout Escape handling stays here; mobile-menu Escape is bound
  // only while the mobile menu is actually open (see bindMobileEscape).
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' || !openFlyoutId) {
      return;
    }
    const trigger = document.querySelector('[data-flyout="' + openFlyoutId + '"]');
    closeAllFlyouts();
    if (trigger) {
      trigger.focus();
    }
  });
}

function openMobileMenu() {
  if (!cachedMobileMenu) {
    return;
  }
  cachedMobileMenu.setAttribute('aria-hidden', 'false');
  const hamburger = document.querySelector('.js_h_mobile_menu');
  if (hamburger) {
    hamburger.setAttribute('aria-expanded', 'true');
  }
  document.body.classList.add('is_mobile_menu_open');
  bindMobileFocusTrap();
  bindMobileEscape();
  announceToScreenReader('Menü geöffnet');
  if (cachedMobileCloseBtn) {
    cachedMobileCloseBtn.focus();
  }
}

function closeMobileMenu() {
  if (!cachedMobileMenu) {
    return;
  }
  // Close any open drilldown panels
  cachedMobileMenu.querySelectorAll('.header_dropdown_nav__mobile_drilldown.is_open').forEach(function (panel) {
    panel.classList.remove('is_open');
    panel.hidden = true;
  });
  cachedMobileMenu.setAttribute('aria-hidden', 'true');
  const hamburger = document.querySelector('.js_h_mobile_menu');
  if (hamburger) {
    hamburger.setAttribute('aria-expanded', 'false');
  }
  document.body.classList.remove('is_mobile_menu_open');
  unbindMobileFocusTrap();
  unbindMobileEscape();
  announceToScreenReader('Menü geschlossen');
  if (hamburger) {
    hamburger.focus();
  }
}

// =========================================================================
// Mobile Drilldowns (category + alle Kategorien + search)
// =========================================================================

function openMobileDrilldown(panel) {
  if (!panel) {
    return;
  }
  panel.hidden = false;
  void panel.offsetHeight;
  panel.classList.add('is_open');
  const title = panel.querySelector('.js_mobile_drilldown_title, .header_dropdown_nav__all_heading');
  if (title && title.textContent.trim()) {
    announceToScreenReader(title.textContent.trim() + ' geöffnet');
  }
  const firstFocusable = panel.querySelector('button, a, input');
  if (firstFocusable) {
    firstFocusable.focus();
  }
}

function closeMobileDrilldown(panel) {
  if (!panel) {
    return;
  }
  panel.classList.remove('is_open');
  panel.hidden = true;
  // Closing the search drilldown mid-load (Escape, programmatic) must drop the
  // pending fetch — otherwise aria-busy and the skeleton stay stuck and a slow
  // response renders into the now-hidden panel. The back/clear buttons already
  // route through clearMobileResults, so this is idempotent for them.
  if (panel.classList.contains('js_mobile_search_drilldown') && cancelMobileSearchLoad) {
    cancelMobileSearchLoad();
  }
}

function initMobileCategoryDrilldown() {
  const drilldownPanel = document.querySelector('.js_mobile_drilldown');
  const drilldownItems = document.querySelector('.js_mobile_drilldown_items');
  const drilldownTitle = document.querySelector('.js_mobile_drilldown_title');
  const drilldownViewAll = document.querySelector('.js_mobile_drilldown_view_all');
  const drilldownBack = document.querySelector('.js_mobile_drilldown_back');

  if (!drilldownPanel || !drilldownItems) {
    return;
  }

  document.querySelectorAll('.js_dropdown_mobile_category').forEach(function (card) {
    card.addEventListener('click', function (e) {
      if (isDesktop()) {
        return;
      }
      e.preventDefault();

      let children = [];
      try {
        children = JSON.parse(card.dataset.children || '[]');
      } catch (err) {
        return;
      }

      const categoryName = card.querySelector('.navigation_category__label');
      const name = categoryName ? categoryName.textContent.trim() : '';

      if (drilldownTitle) {
        drilldownTitle.textContent = name;
      }
      if (drilldownViewAll) {
        const href = card.getAttribute('href');
        if (href && href !== '#') {
          drilldownViewAll.href = href;
          drilldownViewAll.hidden = false;
          drilldownViewAll.setAttribute('aria-label', 'Alle ' + name + '-Produkte ansehen');
        } else {
          console.error('header-dropdown-navigation: source category card has no usable href', card);
          drilldownViewAll.hidden = true;
        }
      }

      drilldownItems.innerHTML = '';
      children.forEach(function (child) {
        const categoryLink = document.createElement('a');
        categoryLink.href = child.url || '#';
        categoryLink.className = 'navigation_category';

        const label = document.createElement('span');
        label.className = 'navigation_category__label';
        label.textContent = child.name || '';
        categoryLink.appendChild(label);

        if (child.imageUrl) {
          const imgWrap = document.createElement('span');
          imgWrap.className = 'navigation_category__image';
          const img = document.createElement('img');
          img.src = child.imageUrl;
          img.alt = child.name || '';
          img.loading = 'lazy';
          imgWrap.appendChild(img);
          categoryLink.appendChild(imgWrap);
        }

        drilldownItems.appendChild(categoryLink);
      });

      openMobileDrilldown(drilldownPanel);
    });
  });

  if (drilldownBack) {
    drilldownBack.addEventListener('click', function () {
      closeMobileDrilldown(drilldownPanel);
    });
  }
}

function initMobileAllCategories() {
  const allCategoriesBtn = document.querySelector('.js_dropdown_nav_all_btn_mobile');
  const allCategoriesPanel = document.querySelector('.js_mobile_all_categories');
  const allCategoriesBack = document.querySelector('.js_mobile_all_categories_back');

  if (!allCategoriesBtn || !allCategoriesPanel) {
    return;
  }

  allCategoriesBtn.addEventListener('click', function (e) {
    e.preventDefault();
    if (isDesktop()) {
      return;
    }
    openMobileDrilldown(allCategoriesPanel);
  });

  if (allCategoriesBack) {
    allCategoriesBack.addEventListener('click', function () {
      closeMobileDrilldown(allCategoriesPanel);
    });
  }
}

function initMobileSearch() {
  const searchDrilldown = document.querySelector('.js_mobile_search_drilldown');
  const mobileSearchTrigger = document.querySelector('.js_dropdown_mobile_search');
  const mobileSearchInput = searchDrilldown ? searchDrilldown.querySelector('.searchbar__input') : null;
  const mobileSearchBack = searchDrilldown ? searchDrilldown.querySelector('.js_searchbar_back') : null;
  const mobileSearchClear = searchDrilldown ? searchDrilldown.querySelector('.js_searchbar_close') : null;
  const mobileSearchResults = document.querySelector('.js_mobile_search_results');
  const mobileSearchPlaceholder = document.querySelector('.js_mobile_search_placeholder');
  const mobileSearchProducts = document.querySelector('.js_mobile_search_products');
  const mobileSearchSuggestions = document.querySelector('.js_mobile_search_suggestions');
  const mobileSearchEchoText = document.querySelector('.js_mobile_search_echo_text');
  const mobileSearchSkeleton = document.querySelector('.js_mobile_search_skeleton');
  const mobileSearchEmpty = document.querySelector('.js_mobile_search_empty');
  const mobileSearchEmptyQuery = mobileSearchEmpty
    ? mobileSearchEmpty.querySelector('.js_mobile_search_empty_query')
    : null;
  const mobileSearchMode = (searchDrilldown && searchDrilldown.dataset.searchMode) || 'kaufen';
  const mobileSectionFormType = SECTION_SEARCH_FORM_TYPE[mobileSearchMode] || null;

  if (!searchDrilldown || !mobileSearchTrigger || !mobileSearchInput) {
    return;
  }

  let debounceTimer = null;
  let abortController = null;
  let isMobileSearchLoading = false;

  mobileSearchTrigger.addEventListener('focus', function () {
    if (isDesktop()) {
      return;
    }
    openMobileDrilldown(searchDrilldown);
    setTimeout(function () {
      mobileSearchInput.focus();
    }, 50);
  });

  mobileSearchInput.addEventListener('input', function () {
    updateMobileSearchView();
  });

  if (mobileSearchBack) {
    mobileSearchBack.addEventListener('click', function () {
      mobileSearchInput.value = '';
      clearMobileResults();
      closeMobileDrilldown(searchDrilldown);
    });
  }

  if (mobileSearchClear) {
    mobileSearchClear.addEventListener('click', function () {
      mobileSearchInput.value = '';
      clearMobileResults();
      updateMobileSearchView();
      mobileSearchInput.focus();
    });
  }

  function updateMobileSearchView() {
    const query = mobileSearchInput.value.trim();
    const hasMinChars = query.length >= SEARCH_MIN_CHARS;

    // Mirror desktop: don't reveal an empty results box for the first one or
    // two characters; Kaufen keeps its placeholder until the search fires. The
    // drilldown itself stays open (the searchbar lives inside it), only the
    // placeholder and results containers toggle.
    if (mobileSearchPlaceholder) {
      mobileSearchPlaceholder.hidden = hasMinChars;
    }
    if (mobileSearchResults) {
      mobileSearchResults.hidden = !hasMinChars;
    }
    if (mobileSearchEchoText) {
      // Wrap the query in German typographic quotes („query“) for visual feedback.
      mobileSearchEchoText.textContent = '\u201E' + query + '\u201C';
    }

    if (hasMinChars) {
      enterMobileSearchLoading();
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      debounceTimer = setTimeout(function () {
        fetchMobileSearchResults(query);
      }, SEARCH_DEBOUNCE_MS);
    } else {
      clearMobileResults();
    }
  }

  // Mirrors the desktop loading flow (see enterSearchLoading): skeleton on the
  // cold load, existing results kept while refining, aria-busy throughout.
  function enterMobileSearchLoading() {
    if (mobileSearchEmpty) {
      mobileSearchEmpty.hidden = true;
    }
    searchDrilldown.setAttribute('aria-busy', 'true');
    if (mobileSearchSkeleton && isMobileSearchResultsEmpty()) {
      if (mobileSearchResults) {
        mobileSearchResults.hidden = true;
      }
      mobileSearchSkeleton.hidden = false;
    }
    if (!isMobileSearchLoading) {
      announceToScreenReader('Suchergebnisse werden geladen');
      isMobileSearchLoading = true;
    }
  }

  function exitMobileSearchLoading() {
    isMobileSearchLoading = false;
    searchDrilldown.setAttribute('aria-busy', 'false');
    if (mobileSearchSkeleton) {
      mobileSearchSkeleton.hidden = true;
    }
  }

  // Drops a pending search without discarding rendered results, so closing the
  // drilldown mid-load leaves nothing dangling. Exposed module-side so the
  // Escape/close paths outside this closure can call it (see closeMobileDrilldown).
  cancelMobileSearchLoad = function () {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    if (abortController) {
      abortController.abort();
    }
    exitMobileSearchLoading();
  };

  function isMobileSearchResultsEmpty() {
    if (mobileSectionFormType) {
      return !mobileSearchResults || mobileSearchResults.children.length === 0;
    }
    return (
      (!mobileSearchProducts || mobileSearchProducts.children.length === 0) &&
      (!mobileSearchSuggestions || mobileSearchSuggestions.children.length === 0)
    );
  }

  // Mirrors showSearchResultsOrEmpty on desktop.
  function showMobileSearchResultsOrEmpty(query) {
    const empty = isMobileSearchResultsEmpty();
    if (mobileSearchResults) {
      mobileSearchResults.hidden = empty;
    }
    if (mobileSearchEmpty) {
      if (mobileSearchEmptyQuery) {
        mobileSearchEmptyQuery.textContent = '„' + query + '“';
      }
      mobileSearchEmpty.hidden = !empty;
    }
    if (empty) {
      announceToScreenReader('Keine Ergebnisse für ' + query);
    }
  }

  function fetchMobileSearchResults(query) {
    if (abortController) {
      abortController.abort();
    }
    abortController = new AbortController();

    const url = mobileSectionFormType
      ? '/findTypes.php?' + SECTION_SEARCH_QUERY_PARAM[mobileSearchMode] + '&query=' + encodeURIComponent(query)
      : '/findTypes.php?big_design_tiles=1&card=search_result&query=' + encodeURIComponent(query);

    fetch(url, {
      signal: abortController.signal,
    })
      .then(function (resp) {
        return resp.json();
      })
      .then(function (data) {
        const results = data.suggestions || [];
        if (mobileSectionFormType) {
          renderMobileSectionResults(results);
        } else {
          renderMobileProducts(results);
          renderMobileSuggestions(results, query);
        }
        showMobileSearchResultsOrEmpty(query);
        exitMobileSearchLoading();
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') {
          return;
        }
        exitMobileSearchLoading();
        console.error('Mobile search failed:', err);
      });
  }

  function clearMobileResults() {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    if (abortController) {
      abortController.abort();
    }
    if (mobileSearchProducts) {
      mobileSearchProducts.innerHTML = '';
    }
    if (mobileSearchSuggestions) {
      mobileSearchSuggestions.innerHTML = '';
    }
    if (mobileSectionFormType && mobileSearchResults) {
      mobileSearchResults.innerHTML = '';
    }
    if (mobileSearchEmpty) {
      mobileSearchEmpty.hidden = true;
    }
    exitMobileSearchLoading();
  }

  function renderMobileSectionResults(results) {
    if (!mobileSearchResults) {
      return;
    }
    mobileSearchResults.innerHTML = '';
    let shown = 0;
    results.forEach(function (item) {
      if (shown >= SECTION_SEARCH_MAX_RESULTS) {
        return;
      }
      mobileSearchResults.appendChild(createSectionSearchCard(item, mobileSectionFormType));
      shown++;
    });
  }

  function renderMobileProducts(results) {
    renderCardHtmlInto(mobileSearchProducts, results, 4);
  }

  function renderMobileSuggestions(results, query) {
    if (!mobileSearchSuggestions) {
      return;
    }
    mobileSearchSuggestions.innerHTML = '';

    let shown = 0;
    results.forEach(function (item) {
      if (shown >= 5 || !item.article_type_name) {
        return;
      }
      const suggestionLink = document.createElement('a');
      suggestionLink.href = item.product_page_url || '/products?s=' + encodeURIComponent(item.article_type_name);
      suggestionLink.className = 'header_dropdown_nav__search_flyout_term';
      suggestionLink.appendChild(createArrowUpRightIcon('header_dropdown_nav__search_flyout_icon'));
      const span = document.createElement('span');
      span.textContent = item.article_type_name;
      suggestionLink.appendChild(span);
      mobileSearchSuggestions.appendChild(suggestionLink);
      shown++;
    });
  }
}

// =========================================================================
// Search
// =========================================================================

function initSearch() {
  const searchbar = document.querySelector('.js_header_searchbar');
  const searchInput = searchbar ? searchbar.querySelector('.searchbar__input') : null;
  const searchClose = searchbar ? searchbar.querySelector('.js_searchbar_close') : null;
  const searchFlyout = document.querySelector('.js_header_search_flyout');
  const searchPlaceholder = document.querySelector('.js_header_search_placeholder');
  const searchResults = document.querySelector('.js_header_search_results');
  const searchEchoText = document.querySelector('.js_header_search_echo_text');
  const searchSuggestions = document.querySelector('.js_header_search_suggestions');
  const searchProducts = document.querySelector('.js_header_search_products');
  const searchSkeleton = document.querySelector('.js_header_search_skeleton');
  const searchEmpty = document.querySelector('.js_header_search_empty');
  const searchEmptyQuery = searchEmpty ? searchEmpty.querySelector('.js_header_search_empty_query') : null;
  const searchMode = (searchFlyout && searchFlyout.dataset.searchMode) || 'kaufen';
  const sectionFormType = SECTION_SEARCH_FORM_TYPE[searchMode] || null;

  let debounceTimer = null;
  let abortController = null;
  let isSearchLoading = false;

  if (!searchbar || !searchInput) {
    return;
  }

  function openSearchFlyout() {
    if (searchFlyout) {
      searchFlyout.hidden = false;
    }
    // Only hide the secondary category nav for the full-width Kaufen
    // flyout, which would otherwise overlap it. The V/R variant sits
    // under the searchbar and doesn't overlap the nav, so the nav stays.
    if (cachedNav && !sectionFormType) {
      cachedNav.classList.add('header_dropdown_nav--hidden_for_search');
    }
    updateSearchView();
    closeAllFlyouts();
  }

  /**
   * Hides the flyout and restores nav visibility without touching the
   * user's query. Used by outside-click — preserving the input lets users
   * resume their search by re-focusing the field.
   */
  function hideSearchFlyout() {
    if (searchFlyout) {
      searchFlyout.hidden = true;
    }
    if (cachedNav) {
      cachedNav.classList.remove('header_dropdown_nav--hidden_for_search');
    }
    // Closing mid-load (outside-click, close button) must drop the pending
    // fetch so aria-busy and the skeleton don't stay stuck and a slow response
    // can't render into the hidden flyout. The query and rendered results stay
    // for re-focus.
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    if (abortController) {
      abortController.abort();
    }
    exitSearchLoading();
  }

  /**
   * Full reset for the explicit close button: clears the input, hides the
   * flyout, drops cached results.
   */
  function closeSearchFlyout() {
    searchInput.value = '';
    searchInput.blur();
    hideSearchFlyout();
    clearResults();
  }

  function updateSearchView() {
    const query = searchInput.value.trim();
    const hasMinChars = query.length >= SEARCH_MIN_CHARS;

    // Below the search threshold we show nothing new: a skeleton here would
    // promise results that aren't being fetched yet. Kaufen keeps its
    // placeholder; V/R has none, so its flyout stays closed instead of
    // flashing an empty white box for the first one or two characters.
    if (searchPlaceholder) {
      searchPlaceholder.hidden = hasMinChars;
    }
    if (searchResults) {
      searchResults.hidden = !hasMinChars;
    }
    if (sectionFormType && searchFlyout) {
      searchFlyout.hidden = !hasMinChars;
    }
    if (searchEchoText) {
      // Wrap the query in German typographic quotes („query“) for visual feedback.
      searchEchoText.textContent = '\u201E' + query + '\u201C';
    }

    if (hasMinChars) {
      enterSearchLoading();
      debounceFetch(query);
    } else {
      clearResults();
    }
  }

  // Shows the skeleton for the whole debounce + network window so the flyout
  // never flashes empty. On a refinement — results already on screen — we keep
  // them visible and only flag aria-busy, so there's no skeleton flicker
  // between keystrokes.
  function enterSearchLoading() {
    if (searchEmpty) {
      searchEmpty.hidden = true;
    }
    if (searchFlyout) {
      searchFlyout.setAttribute('aria-busy', 'true');
    }
    if (searchSkeleton && isSearchResultsEmpty()) {
      if (searchResults) {
        searchResults.hidden = true;
      }
      searchSkeleton.hidden = false;
    }
    if (!isSearchLoading) {
      announceToScreenReader('Suchergebnisse werden geladen');
      isSearchLoading = true;
    }
  }

  function exitSearchLoading() {
    isSearchLoading = false;
    if (searchFlyout) {
      searchFlyout.setAttribute('aria-busy', 'false');
    }
    if (searchSkeleton) {
      searchSkeleton.hidden = true;
    }
  }

  // Element children only, so leftover whitespace text nodes in the
  // server-rendered containers don't read as "already populated".
  function isSearchResultsEmpty() {
    if (sectionFormType) {
      return !searchResults || searchResults.children.length === 0;
    }
    return (
      (!searchSuggestions || searchSuggestions.children.length === 0) &&
      (!searchProducts || searchProducts.children.length === 0)
    );
  }

  // After a render, reveal either the results or the "no results" state — never
  // the bare headings over empty columns, which read as "results are still coming".
  function showSearchResultsOrEmpty(query) {
    const empty = isSearchResultsEmpty();
    if (searchResults) {
      searchResults.hidden = empty;
    }
    if (searchEmpty) {
      if (searchEmptyQuery) {
        searchEmptyQuery.textContent = '„' + query + '“';
      }
      searchEmpty.hidden = !empty;
    }
    if (empty) {
      announceToScreenReader('Keine Ergebnisse für ' + query);
    }
  }

  function debounceFetch(query) {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    debounceTimer = setTimeout(function () {
      fetchSearchResults(query);
    }, SEARCH_DEBOUNCE_MS);
  }

  function fetchSearchResults(query) {
    if (abortController) {
      abortController.abort();
    }
    abortController = new AbortController();

    const url = sectionFormType
      ? '/findTypes.php?' + SECTION_SEARCH_QUERY_PARAM[searchMode] + '&query=' + encodeURIComponent(query)
      : '/findTypes.php?big_design_tiles=1&card=product&query=' + encodeURIComponent(query);

    fetch(url, {
      signal: abortController.signal,
    })
      .then(function (resp) {
        return resp.json();
      })
      .then(function (data) {
        const results = data.suggestions || [];
        if (sectionFormType) {
          renderSectionResults(results);
        } else {
          renderSuggestions(results, query);
          renderProducts(results);
        }
        showSearchResultsOrEmpty(query);
        exitSearchLoading();
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') {
          return;
        }
        exitSearchLoading();
        console.error('Search failed:', err);
      });
  }

  function clearResults() {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    if (abortController) {
      abortController.abort();
    }
    if (searchSuggestions) {
      searchSuggestions.innerHTML = '';
    }
    if (searchProducts) {
      searchProducts.innerHTML = '';
    }
    if (sectionFormType && searchResults) {
      searchResults.innerHTML = '';
    }
    if (searchEmpty) {
      searchEmpty.hidden = true;
    }
    exitSearchLoading();
  }

  function renderSectionResults(results) {
    if (!searchResults) {
      return;
    }
    searchResults.innerHTML = '';
    let shown = 0;
    results.forEach(function (item) {
      if (shown >= SECTION_SEARCH_MAX_RESULTS) {
        return;
      }
      searchResults.appendChild(createSectionSearchCard(item, sectionFormType));
      shown++;
    });
  }

  function renderSuggestions(results, query) {
    if (!searchSuggestions) {
      return;
    }
    searchSuggestions.innerHTML = '';

    let shown = 0;
    results.forEach(function (item) {
      if (shown >= 5 || !item.article_type_name) {
        return;
      }
      const suggestionLink = document.createElement('a');
      suggestionLink.href = item.product_page_url || '/products?s=' + encodeURIComponent(item.article_type_name);
      suggestionLink.className = 'header_dropdown_nav__search_flyout_term';
      suggestionLink.appendChild(createArrowUpRightIcon('header_dropdown_nav__search_flyout_icon'));
      const span = document.createElement('span');
      span.textContent = item.article_type_name;
      suggestionLink.appendChild(span);
      searchSuggestions.appendChild(suggestionLink);
      shown++;
    });
  }

  function renderProducts(results) {
    renderCardHtmlInto(searchProducts, results, 5);
  }

  // On mobile the top-row searchbar is visible but suggestions render in
  // the in-menu drilldown (the desktop flyout is bound to >= the desktop
  // breakpoint). Forward focus into the mobile menu so the existing flow
  // handles fetching + rendering results.
  function handleSearchFocus() {
    if (isDesktop()) {
      openSearchFlyout();
      return;
    }
    const drilldown = document.querySelector('.js_mobile_search_drilldown');
    const drilldownInput = drilldown ? drilldown.querySelector('.searchbar__input') : null;
    if (!drilldown || !drilldownInput) {
      return;
    }
    if (!document.body.classList.contains('is_mobile_menu_open')) {
      openMobileMenu();
    }
    openMobileDrilldown(drilldown);
    // Defer focus past the drilldown reveal so iOS Safari brings up the
    // keyboard for the now-visible input.
    setTimeout(function () {
      drilldownInput.focus();
    }, 50);
  }

  searchInput.addEventListener('focus', handleSearchFocus);
  searchInput.addEventListener('input', updateSearchView);

  searchbar.addEventListener('click', function (e) {
    // Clicks on the input itself trigger the focus listener already; only
    // handle clicks on the surrounding pill (label, icon, padding) here so
    // handleSearchFocus doesn't fire twice on direct input taps.
    if (e.target === searchInput) {
      return;
    }
    if (isDesktop()) {
      searchInput.focus();
    } else {
      handleSearchFocus();
    }
  });

  if (searchClose) {
    searchClose.addEventListener('click', closeSearchFlyout);
  }

  document.addEventListener('click', function (e) {
    if (!searchbar.contains(e.target) && (!searchFlyout || !searchFlyout.contains(e.target))) {
      if (searchFlyout && !searchFlyout.hidden) {
        hideSearchFlyout();
      }
    }
  });
}

// =========================================================================
// Init
// =========================================================================

/**
 * Fills the cart badge from #js-data-container[data-basket-count].
 *
 * The header is cached cross-request by (section, isLoggedIn), so the badge
 * count cannot be rendered server-side or it would be stale for the next
 * user. The data container is part of the body-open fragment, which renders
 * fresh per request, so its data-basket-count attribute is always correct.
 *
 * Runs unconditionally (independent of cachedNav) so the badge hydrates on
 * pages that render a compact-mode header without the dropdown nav too.
 */
function hydrateCartBadge() {
  const dataContainer = document.getElementById('js-data-container');
  const count = parseInt(dataContainer?.dataset.basketCount ?? '0', 10);
  if (!Number.isFinite(count) || count <= 0) {
    return;
  }

  const badge = document.querySelector('.js_header_cart_badge');
  if (badge) {
    badge.textContent = String(count);
    badge.hidden = false;
  }

  const cart = document.querySelector('.js_header_cart');
  if (cart) {
    const label = cart.dataset.cartLabel || cart.getAttribute('aria-label') || '';
    cart.setAttribute('aria-label', `${label} (${count} Artikel)`);
  }
}

function init() {
  hydrateCartBadge();

  cachedNav = document.querySelector('.js_dropdown_nav');
  if (!cachedNav) {
    return;
  }

  // Populate DOM caches BEFORE initSearch(): initSearch wires handlers that
  // call closeAllFlyouts(), which iterates cachedFlyouts/cachedTriggers.
  cachedFlyouts = document.querySelectorAll('.js_dropdown_nav_flyout');
  cachedTriggers = document.querySelectorAll('.js_dropdown_nav_trigger');
  cachedAllCategoriesBtn = document.querySelector('.js_dropdown_nav_all_btn');
  cachedMobileMenu = document.querySelector('.js_dropdown_nav_mobile');
  cachedMobileCloseBtn = document.querySelector('.js_dropdown_nav_mobile_close');

  initSearch();
  initDesktopListeners();
  initDrilldown();
  initMobileMenu();
  initMobileCategoryDrilldown();
  initMobileAllCategories();
  initMobileSearch();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
